/**
 * PrintJobsPage: the store's print queue (FR-PRN-01..05, ADR 0027): every print job with its printer, status,
 * attempts, last error and next attempt, filtered by status (including "Open": queued, printing or failed) and time.
 *
 * Props: `mode`; the print queue exists at the store only (HO has no printers), so 'ho' shows a notice. A failed job
 * has a *Retry* row action (`store.devices.manage`); a job already taken over by a fallback copy answers 409
 * `printing.job_superseded`, which is shown as its own message. Reloads every 15 seconds while the tab is visible.
 * The first filter can come from the URL (`?status=failed`).
 */
import { InlineNotification } from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAdmin } from '../adminContext';
import { ListTable, type ListColumn } from '../components/ListTable';
import { PageHeader } from '../components/PageHeader';
import { RowAction } from '../components/RowAction';
import { DateRangeFilter } from '../fields/DateField';
import { FilterLayout } from '../filters/FilterPanel';
import { ChoiceDropdown } from '../filters/ResourceFilters';
import { useFilters } from '../filters/useFilters';
import { dateRange, useFormat } from '../orders/model';
import { asProblem, problemMessage } from '../problems';
import { hasPermission } from '../session';
import type { PrintJob } from './kitchenApi';
import { JOB_STATUSES, KITCHEN_DEVICES_PERMISSION, KITCHEN_POLL_MS, kitchenKey, useKitchenApi, type KitchenMode } from './model';
import { useStationOptions } from './stations';
import { KitchenStatusTag } from './KitchenStatusTag';
import './kitchen.scss';

const PAGE_SIZES = [25, 50, 100];
const NOTICE_MS = 8000;

export function PrintJobsPage({ mode }: { mode: KitchenMode }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const api = useKitchenApi();
  const queryClient = useQueryClient();
  const format = useFormat();
  const [searchParams] = useSearchParams();
  const stations = useStationOptions(mode);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0] ?? 25);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; title: string }>();
  const filters = useFilters({ status: searchParams.get('status') ?? '', station: '', from: '', to: '' }, () => setPage(1));
  const { status, station, from, to } = filters.applied;
  const canRetry = hasPermission(session, KITCHEN_DEVICES_PERMISSION);

  useEffect(() => {
    if (notice?.kind !== 'success') return undefined;
    const timer = setTimeout(() => setNotice(undefined), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const params = { status: status || undefined, station: station || undefined, ...dateRange(from, to), page, pageSize };
  const list = useQuery({
    queryKey: [...kitchenKey, 'jobs', params],
    queryFn: () => api.jobs(params),
    placeholderData: keepPreviousData,
    enabled: mode === 'store',
    refetchInterval: KITCHEN_POLL_MS,
    refetchIntervalInBackground: false,
  });

  const retry = useMutation({
    mutationFn: (job: PrintJob) => api.retryJob(job.id),
    onSuccess: () => {
      setNotice({ kind: 'success', title: t('admin.kitchen.jobs.retried') });
      void queryClient.invalidateQueries({ queryKey: [...kitchenKey] });
    },
    onError: (error) => {
      const problem = asProblem(error);
      setNotice({
        kind: 'error',
        title:
          problem?.code === 'printing.job_superseded'
            ? t('admin.kitchen.jobs.superseded')
            : problemMessage(t, problem),
      });
    },
  });

  if (mode === 'ho') {
    return (
      <section className="pos-admin-page">
        <PageHeader title={t('admin.kitchen.jobs.title')} />
        <InlineNotification kind="info" lowContrast hideCloseButton title={t('admin.kitchen.jobs.storeOnly')} />
      </section>
    );
  }

  const columns: ListColumn<PrintJob>[] = [
    { key: 'createdAt', header: t('admin.kitchen.jobs.createdAt'), sortable: true, render: (j) => format.dateTime(j.createdAt), sortValue: (j) => j.createdAt },
    { key: 'deviceName', header: t('admin.kitchen.jobs.device'), sortable: true },
    { key: 'status', header: t('admin.kitchen.jobs.status'), render: (j) => <KitchenStatusTag kind="job" status={j.status} /> },
    { key: 'attempts', header: t('admin.kitchen.jobs.attempts'), className: 'pos-admin-page__number', render: (j) => format.number(j.attempts) },
    { key: 'lastError', header: t('admin.kitchen.jobs.error') },
    {
      key: 'nextAttemptAt',
      header: t('admin.kitchen.jobs.nextAttempt'),
      render: (j) => (j.status === 'queued' ? format.dateTime(j.nextAttemptAt) : ''),
    },
  ];
  const statusChoices = [
    { id: 'open', label: t('admin.kitchen.job.open') },
    ...JOB_STATUSES.map((s) => ({ id: s, label: t(`admin.kitchen.job.${s}` as I18nKey) })),
  ];

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('admin.kitchen.jobs.title')} description={t('admin.kitchen.jobs.hint')} />
      {notice && (
        <InlineNotification
          kind={notice.kind}
          role={notice.kind === 'error' ? 'alert' : 'status'}
          lowContrast
          hideCloseButton
          title={notice.title}
        />
      )}
      <FilterLayout
        filters={filters}
        fields={
          <>
            <ChoiceDropdown
              id="jobs-status"
              label={t('admin.kitchen.jobs.status')}
              choices={statusChoices}
              value={filters.draft.status}
              onChange={(v) => filters.set('status', v)}
            />
            <ChoiceDropdown
              id="jobs-station"
              label={t('admin.kitchen.filters.station')}
              choices={stations}
              value={filters.draft.station}
              onChange={(v) => filters.set('station', v)}
            />
            <DateRangeFilter
              id="jobs"
              legendText={t('admin.kitchen.jobs.createdAt')}
              fromLabel={t('admin.filters.from')}
              toLabel={t('admin.filters.to')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(range) => {
                filters.set('from', range.from);
                filters.set('to', range.to);
              }}
            />
          </>
        }
      >
        <ListTable
          ariaLabel={t('admin.kitchen.jobs.title')}
          columns={columns}
          rows={list.data?.items ?? []}
          loading={list.isLoading}
          error={list.error ? problemMessage(t, asProblem(list.error)) : undefined}
          onRetry={() => void list.refetch()}
          filters={filters}
          empty={{ heading: t('admin.kitchen.jobs.empty') }}
          rowActions={(job) =>
            canRetry && job.status === 'failed' ? (
              <RowAction
                action={t('admin.kitchen.jobs.retry')}
                record={job.deviceName ?? job.id}
                icon={Renew}
                onClick={() => retry.mutate(job)}
              />
            ) : null
          }
          pagination={{
            page,
            pageSize,
            pageSizes: PAGE_SIZES,
            total: list.data?.total ?? 0,
            capped: list.data?.totalCapped,
            onChange: ({ page: nextPage, pageSize: nextSize }) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </FilterLayout>
    </section>
  );
}
